import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Agenda local de lembretes (manutenções por data) persistida em localStorage.
 * Funciona 100% no navegador, sem backend, sem login adicional. Cada item
 * tem data alvo e pode ter recorrência em dias.
 *
 * `markCompleted` avança a `dueDate` automaticamente se há recorrência.
 */

export type AgendaStatus = 'overdue' | 'soon' | 'future' | 'completed';

export interface AgendaItem {
  id: string;
  title: string;
  description?: string;
  /** opcional: ID da máquina relacionada (resolvido pelo fleet store na exibição) */
  deviceId?: number;
  /** data alvo no formato YYYY-MM-DD */
  dueDate: string;
  /** intervalo em dias para repetição após conclusão; 0/undef = não repete */
  recurrenceDays?: number;
  /** datas em que o item foi marcado como concluído (mais recente primeiro) */
  completedDates: string[];
  /** ISO da criação */
  createdAt: string;
}

interface AgendaState {
  items: AgendaItem[];
  add: (item: Omit<AgendaItem, 'id' | 'completedDates' | 'createdAt'>) => string;
  update: (id: string, patch: Partial<AgendaItem>) => void;
  remove: (id: string) => void;
  /** marca como feito hoje; se tem recorrência, avança dueDate */
  markCompleted: (id: string) => void;
  snooze: (id: string, days: number) => void;
}

function generateId(): string {
  return `agenda-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export const useAgendaStore = create<AgendaState>()(
  persist(
    (set) => ({
      items: [],
      add: (item) => {
        const id = generateId();
        set((state) => ({
          items: [
            ...state.items,
            {
              ...item,
              id,
              completedDates: [],
              createdAt: new Date().toISOString(),
            },
          ],
        }));
        return id;
      },
      update: (id, patch) =>
        set((state) => ({
          items: state.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
        })),
      remove: (id) =>
        set((state) => ({ items: state.items.filter((it) => it.id !== id) })),
      markCompleted: (id) =>
        set((state) => ({
          items: state.items.map((it) => {
            if (it.id !== id) return it;
            const today = todayIso();
            const nextDue =
              it.recurrenceDays && it.recurrenceDays > 0
                ? addDays(today, it.recurrenceDays)
                : it.dueDate;
            return {
              ...it,
              completedDates: [today, ...it.completedDates].slice(0, 50),
              dueDate: nextDue,
            };
          }),
        })),
      snooze: (id, days) =>
        set((state) => ({
          items: state.items.map((it) =>
            it.id === id ? { ...it, dueDate: addDays(it.dueDate, days) } : it,
          ),
        })),
    }),
    {
      name: 'fismatek-agenda',
    },
  ),
);

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Calcula status do item com base na dueDate.
 * - overdue: dueDate já passou
 * - soon: dueDate em ≤ 7 dias
 * - future: dueDate em mais de 7 dias
 * Itens com `completedDates` recentes mas sem recorrência mostram completed.
 */
export function getAgendaStatus(item: AgendaItem): AgendaStatus {
  if (
    !item.recurrenceDays &&
    item.completedDates.length > 0 &&
    item.completedDates[0] >= item.dueDate
  ) {
    return 'completed';
  }
  const target = new Date(`${item.dueDate}T23:59:59`).getTime();
  const diffDays = Math.floor((target - Date.now()) / DAY_MS);
  if (diffDays < 0) return 'overdue';
  if (diffDays <= 7) return 'soon';
  return 'future';
}

export function getDaysUntil(iso: string): number {
  const target = new Date(`${iso}T23:59:59`).getTime();
  return Math.floor((target - Date.now()) / DAY_MS);
}
